import { useCallback, useEffect, useState } from "react";
import { api, API, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import ErgonomicTableContainer from "../components/common/ErgonomicTableContainer";
import DocumentPreviewModal, { friendlyFilename } from "../components/DocumentPreviewModal";
import RejectReasonPicker from "../components/RejectReasonPicker";
import { FileText, Image as ImageIcon, Eye, Download, Check, X, ShieldCheck, AlertTriangle, Search, RotateCcw, IndianRupee, CheckCircle2, Clock, MinusCircle, Loader2, Award, ExternalLink, Upload, Plus } from "lucide-react";
import { format, subDays, startOfMonth } from "date-fns";
import { toast } from "sonner";

const FILES_ORIGIN = API.replace(/\/api\/?$/, "");

export const STAGES = [
  { id: "lead", label: "Lead Captured" },
  { id: "application_initiated", label: "Application Initiated" },
  { id: "doc_verified", label: "Document Verified" },
  { id: "offer_issued", label: "Offer Issued" },
  { id: "fee_paid", label: "Fee Paid" },
  { id: "enrolled", label: "Enrolled" },
];

const STAGE_COLORS = {
  lead: "border-t-slate-400",
  application_initiated: "border-t-blue-500",
  doc_verified: "border-t-indigo-500",
  offer_issued: "border-t-violet-500",
  fee_paid: "border-t-teal-500",
  enrolled: "border-t-emerald-600",
};

const STAFF_DOC_TYPES = [
  { key: "10th_marksheet", label: "10th Mark Sheet" },
  { key: "12th_marksheet", label: "12th Mark Sheet" },
  { key: "id_proof", label: "Identification Proof" },
  { key: "transfer_certificate", label: "Transfer Certificate" },
  { key: "other", label: "Other Document" },
];

const DOC_STATUS = {
  verified: { label: "Verified", cls: "bg-emerald-100 text-emerald-800", icon: ShieldCheck },
  discrepancy: { label: "Discrepancy", cls: "bg-red-100 text-red-800", icon: AlertTriangle },
  pending_review: { label: "Pending review", cls: "bg-amber-100 text-amber-800", icon: FileText },
  no_documents: { label: "No documents", cls: "bg-slate-100 text-slate-500", icon: FileText },
};
const DOC_ITEM = {
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  pending: "bg-amber-100 text-amber-800",
};

function DocBadge({ app, onClick, testid }) {
  const meta = DOC_STATUS[app.overall_doc_status] || DOC_STATUS.no_documents;
  const s = app.doc_summary || {};
  const Icon = meta.icon;
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 transition ${meta.cls}`}>
      <Icon className="w-3.5 h-3.5" />
      {meta.label}
      {s.total ? <span className="font-mono opacity-70">{s.approved}/{s.total}</span> : null}
    </button>
  );
}

function FeeBadge({ app, onClick, testid }) {
  const paid = app.fee_status === "paid";
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 transition ${
        paid ? "bg-teal-100 text-teal-800" : "bg-slate-100 text-slate-600"
      }`}>
      <IndianRupee className="w-3.5 h-3.5" />
      {paid ? "Fee Paid" : "Fee Due"}
    </button>
  );
}

function OfferBadge({ app, onClick, testid }) {
  const eligible = app.overall_doc_status === "verified";
  const issued = Boolean(app.offer_letter?.serial_number);
  const label = issued ? "Offer Issued" : eligible ? "Issue Offer" : "Docs Pending";
  const cls = issued
    ? "bg-indigo-100 text-indigo-800"
    : eligible
      ? "bg-blue-100 text-blue-800"
      : "bg-slate-100 text-slate-500";
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 transition ${cls}`}>
      <Award className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

// window.location.origin rather than a hardcoded crm.wizardsoftware.in — same
// resulting URL in production, but still correct if this ever runs on a
// tenant subdomain or a staging/local origin.
const trackerUrl = (token) => `${window.location.origin}/track/${token}`;

/** Small icon-only "open the candidate's public tracker" button, for dense
 * contexts (table rows, kanban cards) where a text label doesn't fit.
 * stopPropagation so it never bubbles into a row/card click handler and
 * toggles a drawer instead of just opening the new tab. */
function OpenTrackerIconButton({ token, testid }) {
  if (!token) return null;
  return (
    <a
      href={trackerUrl(token)} target="_blank" rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title="Open public tracking portal" data-testid={testid}
      className="inline-flex items-center justify-center w-6 h-6 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors shrink-0"
    >
      <ExternalLink className="w-3.5 h-3.5" />
    </a>
  );
}

/** Pill variant for the Kanban card footer — same link, more affordance
 * since a card has room for a label. */
function OpenTrackerPill({ token, testid }) {
  if (!token) return null;
  return (
    <a
      href={trackerUrl(token)} target="_blank" rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      data-testid={testid}
      className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-700 border border-slate-200 rounded-full px-2 py-0.5 hover:bg-slate-50 transition-colors"
    >
      <ExternalLink className="w-3 h-3" /> Open Tracker
    </a>
  );
}

/** Drawer-header variant — positioned to sit just left of SheetContent's own
 * built-in close button (absolute right-4 top-4, see ui/sheet.jsx), rather
 * than inside the normal document flow, so it reads as part of the header
 * chrome rather than another line of content. */
function ViewPublicPortalLink({ token }) {
  if (!token) return null;
  return (
    <a
      href={trackerUrl(token)} target="_blank" rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="absolute right-11 top-4 text-xs font-medium text-slate-500 hover:text-slate-700 flex items-center gap-1"
      data-testid="view-public-portal-link"
    >
      View Public Portal <span aria-hidden="true">↗</span>
    </a>
  );
}

const DOC_FILTER_OPTS = [
  { v: "all", label: "All Docs" },
  { v: "pending_review", label: "Pending Review" },
  { v: "discrepancy", label: "Discrepancy (Action Needed)" },
  { v: "verified", label: "Verified" },
  { v: "no_documents", label: "No Documents" },
];
const EMPTY_FILTERS = { stage: "all", doc_status: "all", fee_status: "all", course: "all" };
const EMPTY_DATES = { from: "", to: "" };
const iso = (d) => format(d, "yyyy-MM-dd");
const DATE_PRESETS = [
  { v: "all", label: "Any date" },
  { v: "today", label: "Today" },
  { v: "7d", label: "Last 7 days" },
  { v: "month", label: "This month" },
  { v: "custom", label: "Custom range" },
];
function presetRange(v) {
  const now = new Date();
  if (v === "today") return { from: iso(now), to: iso(now) };
  if (v === "7d") return { from: iso(subDays(now, 6)), to: iso(now) };
  if (v === "month") return { from: iso(startOfMonth(now)), to: iso(now) };
  return EMPTY_DATES;
}

export default function Applications() {
  const [apps, setApps] = useState([]);
  const [total, setTotal] = useState(0);
  const [wbFor, setWbFor] = useState(null);
  const [feeFor, setFeeFor] = useState(null);
  const [offerFor, setOfferFor] = useState(null);
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState("");
  const [dSearch, setDSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [dates, setDates] = useState(EMPTY_DATES);
  const [preset, setPreset] = useState("all");

  // debounce the search box
  useEffect(() => {
    const t = setTimeout(() => setDSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const params = { search: dSearch || undefined };
  Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
  if (dates.from) params.from_date = dates.from;
  if (dates.to) params.to_date = dates.to;

  const fetchApps = useCallback(() => {
    api.get("/applications", { params })
      .then((r) => { setApps(r.data.applications || []); setTotal(r.data.total ?? 0); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dSearch, filters.stage, filters.doc_status, filters.fee_status, filters.course, dates.from, dates.to]);

  useEffect(() => { fetchApps(); }, [fetchApps]);
  useEffect(() => {
    api.get("/analytics/filter-options").then((r) => setCourses(r.data.courses || [])).catch(() => {});
  }, []);

  const datesActive = Boolean(dates.from || dates.to);
  const activeCount = (dSearch ? 1 : 0)
    + Object.values(filters).filter((v) => v && v !== "all").length
    + (datesActive ? 1 : 0);
  const clearFilters = () => {
    setSearch(""); setDSearch(""); setFilters(EMPTY_FILTERS); setDates(EMPTY_DATES); setPreset("all");
  };
  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const applyPreset = (v) => { setPreset(v); if (v !== "custom") setDates(presetRange(v)); };
  const setDate = (k) => (val) => { setPreset("custom"); setDates((d) => ({ ...d, [k]: val })); };

  const move = async (appId, stage) => {
    try {
      await api.patch(`/applications/${appId}/stage`, { stage });
      toast.success(`Moved to ${STAGES.find((s) => s.id === stage)?.label}`);
      fetchApps();
    } catch {
      toast.error("Stage update failed");
    }
  };

  const byStage = (id) => apps.filter((a) => a.stage === id);

  const stageSelect = (a, testid) => (
    <Select value={a.stage} onValueChange={(v) => move(a.id, v)}>
      <SelectTrigger data-testid={testid} className="h-8 w-full text-xs bg-white">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div data-testid="applications-page" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Application Pipeline</h1>
        <p className="text-sm text-slate-500 mt-1">Stage tracker from first inquiry to enrollment. Click a document badge to open the verification workbench.</p>
      </div>

      <Tabs defaultValue="kanban">
        <TabsList>
          <TabsTrigger value="kanban" data-testid="applications-tab-kanban">Kanban Board</TabsTrigger>
          <TabsTrigger value="table" data-testid="applications-tab-table">Table View</TabsTrigger>
        </TabsList>

        <Card className="p-3 mt-4 border-slate-200" data-testid="applications-filter-bar">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input data-testid="applications-search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, or mobile..." className="pl-8 h-9 text-sm bg-white" />
            </div>

            <Select value={filters.stage} onValueChange={setF("stage")}>
              <SelectTrigger data-testid="applications-filter-stage" className="w-[170px] h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={filters.doc_status} onValueChange={setF("doc_status")}>
              <SelectTrigger data-testid="applications-filter-docs" className="w-[210px] h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_FILTER_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={filters.fee_status} onValueChange={setF("fee_status")}>
              <SelectTrigger data-testid="applications-filter-fee" className="w-[120px] h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Fee: All</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.course} onValueChange={setF("course")}>
              <SelectTrigger data-testid="applications-filter-course" className="w-[150px] h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courses</SelectItem>
                {courses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={preset} onValueChange={applyPreset}>
              <SelectTrigger data-testid="applications-filter-date-preset" className="w-[140px] h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DATE_PRESETS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-1.5">
              <input type="date" data-testid="applications-from-date" value={dates.from} max={dates.to || undefined}
                onChange={(e) => setDate("from")(e.target.value)}
                className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700" aria-label="From date" />
              <span className="text-xs text-slate-400">–</span>
              <input type="date" data-testid="applications-to-date" value={dates.to} min={dates.from || undefined}
                onChange={(e) => setDate("to")(e.target.value)}
                className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700" aria-label="To date" />
            </div>

            {activeCount > 0 && (
              <Button variant="ghost" size="sm" data-testid="applications-clear-filters"
                onClick={clearFilters} className="text-slate-500">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Clear Filters ({activeCount})
              </Button>
            )}

            <span className="text-xs text-slate-500 sm:ml-auto" data-testid="applications-result-count">
              Showing <span className="font-mono">{apps.length}</span> of <span className="font-mono">{total}</span> applications
            </span>
          </div>
        </Card>

        <TabsContent value="kanban" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="kanban-board">
            {STAGES.map((s) => (
              <div key={s.id} className={`bg-white border border-slate-200 border-t-4 ${STAGE_COLORS[s.id]} rounded-lg p-3 min-h-[300px]`}
                data-testid={`kanban-col-${s.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{s.label}</span>
                  <Badge variant="secondary" className="font-mono">{byStage(s.id).length}</Badge>
                </div>
                <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                  {byStage(s.id).map((a, i) => (
                    <Card key={a.id} className="p-3 border-slate-200 shadow-none" data-testid={`kanban-card-${s.id}-${i}`}>
                      <div className="text-sm font-medium text-slate-800">{a.applicant_name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{a.course}{a.specialization ? ` · ${a.specialization}` : ""}</div>
                      <div className="text-[10px] text-slate-400 mt-1">via {a.source} · {a.campus}</div>
                      <div className="flex items-center gap-2 flex-wrap mt-2">
                        <FeeBadge app={a} onClick={() => setFeeFor(a)} testid={`kanban-fee-${s.id}-${i}`} />
                        <DocBadge app={a} onClick={() => setWbFor(a)} testid={`kanban-docs-${s.id}-${i}`} />
                        <OfferBadge app={a} onClick={() => setOfferFor(a)} testid={`kanban-offer-${s.id}-${i}`} />
                      </div>
                      <div className="mt-2">{stageSelect(a, `kanban-move-${s.id}-${i}`)}</div>
                      <div className="mt-2 pt-2 border-t border-slate-100 flex justify-end">
                        <OpenTrackerPill token={a.tracking_token} testid={`kanban-open-tracker-${s.id}-${i}`} />
                      </div>
                    </Card>
                  ))}
                  {byStage(s.id).length === 0 && <div className="text-xs text-slate-300 text-center py-6">No applications</div>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="table" className="mt-4">
          <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid="applications-table-card">
            {/* Own scroll region, independent of the page (Layout's <main> clips
                horizontal overflow so *something* below it must own the scrollbar).
                A raw <table> here, not the shadcn <Table> wrapper — that component
                nests its own `overflow-auto` div, which fights this one and was the
                root cause of the same squash/clip bug on the Leads page; see
                Leads.jsx for the identical fix.
                ErgonomicTableContainer owns the vertical clamp (so the scrollbar
                stays pinned on screen instead of sinking below dozens of rows) plus
                a synced top scrollbar so staff can pan horizontally without
                scrolling down to find the bottom one first. Application No /
                Applicant stay pinned left (`sticky left-0` / `left-[130px]`) while
                panning across the inspection columns; there's no separate Actions
                column in this table's design to pin right — Stage/Fee/Documents/
                Offer are each themselves the click target, so nothing was added
                there rather than invent a redundant column. */}
            <ErgonomicTableContainer
              maxHeight="calc(100vh - 290px)"
              minWidth="1350px"
              testId="applications-table-scroll"
            >
              <table
                className="w-full text-sm min-w-[1350px] border-collapse
                  [&_th]:px-3 [&_th]:h-11 [&_th]:whitespace-nowrap [&_th]:sticky [&_th]:top-0 [&_th]:z-20
                  [&_th]:bg-slate-50 [&_th]:shadow-[inset_0_-1px_0_#e2e8f0] [&_th]:text-left
                  [&_td]:px-3 [&_td]:py-3 [&_td]:align-middle [&_td]:whitespace-nowrap [&_tr]:border-b [&_tr]:border-slate-100"
              >
                <TableHeader>
                  {/* The two pinned-column headers need `!z-30`/`!bg-slate-100`
                      (not plain z-30/bg-slate-100) because the blanket
                      `[&_th]:z-20 [&_th]:bg-slate-50` rule above compiles to a
                      "parent-class th" compound selector, whose specificity
                      beats a same-weight utility class applied directly on the
                      element — without `!important` the blanket z-20/slate-50
                      would win and these two headers wouldn't out-rank the
                      sticky-top row at the corner intersection while scrolling
                      both ways at once. */}
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="sticky left-0 !z-30 !bg-slate-100 w-[130px] text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Application No
                    </TableHead>
                    <TableHead className="sticky left-[130px] !z-30 !bg-slate-100 w-[180px] shadow-[4px_0_6px_-2px_rgba(0,0,0,0.06)] text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Applicant
                    </TableHead>
                    {[
                      ["Course", "min-w-[160px]"], ["Source", "min-w-[110px]"], ["Campus", "min-w-[110px]"],
                      ["Stage", "min-w-[190px]"], ["Fee", "min-w-[120px]"], ["Documents", "min-w-[120px]"],
                      ["Offer", "min-w-[120px]"], ["Updated", "min-w-[100px]"],
                    ].map(([h, w]) => (
                      <TableHead key={h} className={`${w} text-xs font-semibold uppercase tracking-wider text-slate-500`}>{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apps.map((a, i) => (
                    <TableRow key={a.id} data-testid={`application-row-${i}`} className="group hover:bg-slate-50/60">
                      <TableCell className="sticky left-0 z-10 w-[130px] bg-white group-hover:bg-slate-50 font-mono text-xs text-slate-600">
                        <span className="block truncate">{a.application_no || "—"}</span>
                      </TableCell>
                      <TableCell className="sticky left-[130px] z-10 w-[180px] bg-white group-hover:bg-slate-50 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.06)]">
                        <div className="flex items-center gap-1 min-w-0">
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-slate-800 truncate">{a.applicant_name}</div>
                            <div className="text-xs text-slate-400 truncate">{a.email}</div>
                          </div>
                          <OpenTrackerIconButton token={a.tracking_token} testid={`application-open-tracker-${i}`} />
                        </div>
                      </TableCell>
                      <TableCell>{a.course}{a.specialization ? ` · ${a.specialization}` : ""}</TableCell>
                      <TableCell className="text-slate-600">{a.source}</TableCell>
                      <TableCell className="text-slate-600">{a.campus}</TableCell>
                      <TableCell className="w-48">{stageSelect(a, `application-stage-select-${i}`)}</TableCell>
                      <TableCell><FeeBadge app={a} onClick={() => setFeeFor(a)} testid={`application-fee-${i}`} /></TableCell>
                      <TableCell><DocBadge app={a} onClick={() => setWbFor(a)} testid={`application-docs-${i}`} /></TableCell>
                      <TableCell><OfferBadge app={a} onClick={() => setOfferFor(a)} testid={`application-offer-${i}`} /></TableCell>
                      <TableCell className="text-xs text-slate-400 font-mono">{a.updated_at?.slice(0, 10)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </table>
            </ErgonomicTableContainer>
          </Card>
        </TabsContent>
      </Tabs>

      <Sheet open={!!wbFor} onOpenChange={(o) => !o && setWbFor(null)}>
        <SheetContent className="w-full sm:w-[560px] sm:max-w-[560px] overflow-y-auto" data-testid="doc-workbench">
          {wbFor && <Workbench appId={wbFor.id} trackingToken={wbFor.tracking_token} onChanged={fetchApps} />}
        </SheetContent>
      </Sheet>

      <Sheet open={!!feeFor} onOpenChange={(o) => !o && setFeeFor(null)}>
        <SheetContent className="w-full sm:w-[480px] sm:max-w-[480px] overflow-y-auto" data-testid="fee-drawer">
          {feeFor && <FeeMilestones appId={feeFor.id} trackingToken={feeFor.tracking_token} />}
        </SheetContent>
      </Sheet>

      <Sheet open={!!offerFor} onOpenChange={(o) => !o && setOfferFor(null)}>
        <SheetContent className="w-full sm:w-[480px] sm:max-w-[480px] overflow-y-auto" data-testid="offer-drawer">
          {offerFor && <OfferLetterPanel app={offerFor} onChanged={fetchApps} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Workbench({ appId, trackingToken, onChanged }) {
  const [data, setData] = useState(null);
  const [rejecting, setRejecting] = useState(null); // doc id being rejected
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadType, setUploadType] = useState(STAFF_DOC_TYPES[0].key);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.get(`/applications/${appId}/documents`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [appId]);
  useEffect(() => { load(); }, [load]);

  const review = async (docId, status, rejection_reason) => {
    setBusy(true);
    try {
      await api.patch(`/applications/${appId}/documents/${docId}`, { status, rejection_reason });
      setRejecting(null); setReason("");
      const studentName = data?.applicant_name || "the candidate";
      await load();
      onChanged();
      toast.success(
        status === "approved" || status === "rejected"
          ? `Status updated & notification queued for ${studentName}`
          : `Document ${status}`
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const previewApprove = async (docId) => {
    await review(docId, "approved");
    setPreviewDoc(null);
  };
  const previewReject = async (docId, rejection_reason) => {
    await review(docId, "rejected", rejection_reason);
    setPreviewDoc(null);
  };

  const verifyAll = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/applications/${appId}/documents/verify-all`);
      await load();
      onChanged();
      toast.success(res.approved ? `${res.approved} document(s) approved` : "Nothing left to approve");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const uploadDocument = async () => {
    if (!uploadFile) { toast.error("Choose a file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", uploadType);
      fd.append("file", uploadFile);
      await api.post(`/applications/${appId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadOpen(false);
      setUploadFile(null);
      setUploadType(STAFF_DOC_TYPES[0].key);
      await load();
      onChanged();
      toast.success("Document uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not upload document");
    } finally { setUploading(false); }
  };

  if (!data) {
    return <div className="py-24 flex justify-center"><div className="w-6 h-6 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin" /></div>;
  }

  const meta = DOC_STATUS[data.overall_doc_status] || DOC_STATUS.no_documents;
  const anyPending = data.documents.some((d) => d.status === "pending");

  return (
    <>
      <ViewPublicPortalLink token={trackingToken} />
      <SheetHeader>
        <SheetTitle className="font-display">Document Verification</SheetTitle>
        <div className="text-sm text-slate-500">{data.applicant_name} · {data.course}</div>
        <div className="flex items-center gap-2 mt-1">
          <Badge className={meta.cls} data-testid="workbench-overall-status">{meta.label}</Badge>
          {anyPending && (
            <Button size="sm" variant="outline" data-testid="workbench-verify-all" disabled={busy} onClick={verifyAll}>
              <Check className="w-3.5 h-3.5 mr-1" /> Approve all pending
            </Button>
          )}
          <Button size="sm" variant="outline" className="ml-auto" data-testid="workbench-upload-toggle"
            onClick={() => setUploadOpen((v) => !v)}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Upload Document
          </Button>
        </div>
      </SheetHeader>

      {uploadOpen && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2.5" data-testid="workbench-upload-form">
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <div className="text-xs font-medium text-slate-600 mb-1">Document type</div>
              <Select value={uploadType} onValueChange={setUploadType}>
                <SelectTrigger data-testid="workbench-upload-doctype"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAFF_DOC_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-600 mb-1">File (PDF / JPG / PNG / WEBP)</div>
              <input type="file" accept=".pdf,image/jpeg,image/png,image/webp"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="text-sm w-full" data-testid="workbench-upload-file" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="bg-brand" disabled={uploading || !uploadFile}
              data-testid="workbench-upload-submit" onClick={uploadDocument}>
              <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? "Uploading…" : "Upload"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setUploadOpen(false); setUploadFile(null); }}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="mt-5 space-y-3">
        {data.documents.length === 0 && (
          <div className="text-sm text-slate-400 text-center py-10" data-testid="workbench-empty">
            No documents uploaded for this application.
          </div>
        )}
        {data.documents.map((d, i) => {
          const isImg = (d.file_type || "").startsWith("image/");
          const Icon = isImg ? ImageIcon : FileText;
          return (
            <div key={d.id} className="rounded-lg border border-slate-200 p-3" data-testid={`workbench-doc-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-slate-400 shrink-0" />
                    <span className="text-sm font-medium text-slate-800 truncate">{d.title}</span>
                    <Badge className={DOC_ITEM[d.status]} data-testid={`workbench-doc-status-${i}`}>{d.status}</Badge>
                  </div>
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" data-testid={`workbench-view-${i}`}
                      onClick={() => setPreviewDoc(d)}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> View
                    </Button>
                    <a href={`${FILES_ORIGIN}${d.file_url}?download=true&filename=${encodeURIComponent(friendlyFilename(data.applicant_name, d))}`}
                      data-testid={`workbench-download-${i}`}>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs">
                        <Download className="w-3.5 h-3.5 mr-1" /> Download
                      </Button>
                    </a>
                  </div>
                </div>
              </div>

              {(d.metadata?.claimed_score || d.metadata?.board || d.metadata?.year) && (
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs bg-slate-50 rounded-md px-3 py-2" data-testid={`workbench-claimed-${i}`}>
                  <span className="text-slate-400 uppercase tracking-wider text-[10px] w-full">Applicant-claimed</span>
                  {d.metadata.claimed_score && <span className="text-slate-700">Score: <b>{d.metadata.claimed_score}</b></span>}
                  {d.metadata.board && <span className="text-slate-700">Board: <b>{d.metadata.board}</b></span>}
                  {d.metadata.year && <span className="text-slate-700">Year: <b>{d.metadata.year}</b></span>}
                </div>
              )}

              {d.status === "rejected" && d.rejection_reason && (
                <div className="mt-2 text-xs text-red-700 bg-red-50 rounded-md px-3 py-2" data-testid={`workbench-reason-${i}`}>
                  Rejected: {d.rejection_reason}
                </div>
              )}

              {rejecting === d.id ? (
                <div className="mt-3 space-y-2" data-testid={`workbench-reject-form-${i}`}>
                  <RejectReasonPicker value={reason} onChange={setReason} testIdPrefix={`workbench-reason-${i}`} />
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-red-600 hover:bg-red-700" disabled={busy || !reason.trim()}
                      data-testid={`workbench-reject-confirm-${i}`}
                      onClick={() => review(d.id, "rejected", reason.trim())}>Confirm rejection</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setRejecting(null); setReason(""); }}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" className="text-emerald-700" disabled={busy || d.status === "approved"}
                    data-testid={`workbench-approve-${i}`} onClick={() => review(d.id, "approved")}>
                    <Check className="w-3.5 h-3.5 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" disabled={busy}
                    data-testid={`workbench-reject-${i}`} onClick={() => { setRejecting(d.id); setReason(d.rejection_reason || ""); }}>
                    <X className="w-3.5 h-3.5 mr-1" /> {d.status === "rejected" ? "Edit rejection" : "Reject"}
                  </Button>
                  {(d.reviewed_at) && (
                    <span className="text-[11px] text-slate-400 self-center ml-auto">
                      reviewed {d.reviewed_at.slice(0, 10)}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc}
          applicantName={data.applicant_name}
          filesOrigin={FILES_ORIGIN}
          busy={busy}
          onClose={() => setPreviewDoc(null)}
          onApprove={previewApprove}
          onReject={previewReject}
        />
      )}
    </>
  );
}

const MS_STATUS = {
  paid: { label: "Paid", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", Icon: CheckCircle2 },
  pending: { label: "Pending verification", cls: "text-amber-700 bg-amber-50 border-amber-200", Icon: Clock },
  unpaid: { label: "Unpaid", cls: "text-slate-500 bg-slate-50 border-slate-200", Icon: MinusCircle },
  refunded: { label: "Refunded", cls: "text-red-700 bg-red-50 border-red-200", Icon: RotateCcw },
};
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function FeeMilestones({ appId, trackingToken }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setData(null); setErr("");
    api.get(`/payments/applications/${appId}/fee-milestones`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(formatApiError(e.response?.data?.detail) || "Could not load fee status"); });
    return () => { alive = false; };
  }, [appId]);

  if (err) return <div className="pt-10 text-sm text-red-600">{err}</div>;
  if (!data) return <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <>
      <ViewPublicPortalLink token={trackingToken} />
      <SheetHeader>
        <SheetTitle className="font-display">Fee Progress</SheetTitle>
      </SheetHeader>
      <div className="mt-1 text-sm text-slate-500">{data.applicant_name} · {data.course}</div>

      <div className="mt-5 space-y-3">
        {data.milestones.map((m) => {
          const meta = MS_STATUS[m.status] || MS_STATUS.unpaid;
          return (
            <div key={m.fee_type} className={`rounded-lg border p-3 ${meta.cls}`} data-testid={`milestone-${m.fee_type}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-slate-800">{m.label}</div>
                <div className="font-mono font-semibold text-slate-900">{money(m.total)}</div>
              </div>
              <div className="mt-1 flex items-center justify-between gap-2 text-xs">
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <meta.Icon className="w-3.5 h-3.5" /> {meta.label}{m.estimated ? " (estimated)" : ""}
                </span>
                <span className="text-slate-500">
                  {m.status === "paid" && m.txn_id ? `${m.paid_via || ""} · ${m.txn_id}`
                    : m.status === "pending" && m.utr_ref ? `${m.paid_via || ""} · UTR ${m.utr_ref}`
                    : m.status === "refunded" ? (m.refund_reason || "Refunded")
                    : `base ${money(m.base)} + GST ${money(m.gst)}`}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 pt-4 border-t border-slate-200 flex items-center justify-between text-sm">
        <span className="text-slate-500">Total verified</span>
        <span className="font-mono font-bold text-slate-900">{money(data.total_paid)}</span>
      </div>
    </>
  );
}

// Standard institution fee schedule (mirrors backend routes_payments.FEE_SCHEDULE) —
// used to prefill Base Tuition / Seat Deposit on a first issue, same as the
// Offline Deposit form on the Payments page.
const OFFER_DEFAULT_TUITION = 85000;
const OFFER_DEFAULT_SEAT_DEPOSIT = 50000;

function OfferLetterPanel({ app, onChanged }) {
  const initial = app.offer_letter || null;
  const eligible = app.overall_doc_status === "verified";
  const [issued, setIssued] = useState(initial);
  const [editing, setEditing] = useState(!initial);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [form, setForm] = useState({
    base_tuition: initial?.fee_details?.base_tuition ?? OFFER_DEFAULT_TUITION,
    scholarship_amount: initial?.fee_details?.scholarship_amount ?? 0,
    seat_deposit: initial?.fee_details?.seat_deposit ?? OFFER_DEFAULT_SEAT_DEPOSIT,
    due_date: initial?.fee_details?.due_date ?? "",
    academic_year: initial?.fee_details?.academic_year ?? "",
    apply_gst: initial?.fee_details?.apply_gst ?? true,
  });

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Accepts the just-issued serial explicitly — setIssued() above it hasn't
  // flushed yet when called from submit(), so the panel's own `issued` state
  // would still be stale.
  const download = async (serialForFilename) => {
    setDownloading(true);
    try {
      const r = await api.get(`/applications/${app.id}/offer-letter`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `offer_letter_${(serialForFilename || issued?.serial_number || app.id).replace(/[/\s]/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { apply_gst: !!form.apply_gst };
      if (form.base_tuition !== "") body.base_tuition = Number(form.base_tuition);
      if (form.scholarship_amount !== "") body.scholarship_amount = Number(form.scholarship_amount);
      if (form.seat_deposit !== "") body.seat_deposit = Number(form.seat_deposit);
      if (form.due_date.trim()) body.due_date = form.due_date.trim();
      if (form.academic_year.trim()) body.academic_year = form.academic_year.trim();
      const { data } = await api.post(`/applications/${app.id}/offer-letter`, body);
      setIssued(data);
      setEditing(false);
      const studentName = app.applicant_name || "the candidate";
      toast.success(
        `Status updated & notification queued for ${studentName}`
        + (data.revision > 1 ? ` (offer reissued, rev ${data.revision})` : " (offer issued)")
        + (data.stage === "offer_issued" ? " · stage moved to Offer Issued" : "")
      );
      onChanged();
      await download(data.serial_number); // auto-trigger the PDF download
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <ViewPublicPortalLink token={app.tracking_token} />
      <SheetHeader>
        <SheetTitle className="font-display flex items-center gap-2"><Award className="w-4 h-4" /> Admission Offer Letter</SheetTitle>
        <div className="text-sm text-slate-500">{app.applicant_name} · {app.course}{app.specialization ? ` · ${app.specialization}` : ""}</div>
      </SheetHeader>

      {!eligible && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800" data-testid="offer-ineligible">
          All submitted documents must be verified in the Document Workbench before an offer letter can be issued.
        </div>
      )}

      {issued && !editing && (
        <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4" data-testid="offer-issued-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Reference Number</div>
              <div className="font-mono font-bold text-indigo-900">{issued.serial_number}</div>
            </div>
            <Badge className="bg-indigo-100 text-indigo-800">Rev {issued.revision}</Badge>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-slate-600">
            <div>Base tuition: <b className="text-slate-800">{money(issued.fee_details?.base_tuition)}</b></div>
            <div>Scholarship: <b className="text-slate-800">{money(issued.fee_details?.scholarship_amount)}</b></div>
            <div>Seat deposit: <b className="text-slate-800">{money(issued.fee_details?.seat_deposit)}</b></div>
            <div>Due by: <b className="text-slate-800">{issued.fee_details?.due_date}</b></div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button className="bg-brand flex-1" disabled={downloading} onClick={() => download()} data-testid="offer-download">
              {downloading ? "Preparing…" : "Download PDF"}
            </Button>
            <Button variant="outline" onClick={() => setEditing(true)} data-testid="offer-edit">Reissue / Edit</Button>
          </div>
        </div>
      )}

      {(editing || !issued) && (
        <form onSubmit={submit} className="mt-4 space-y-3" data-testid="offer-form">
          {issued && (
            <p className="text-xs text-slate-500">
              Editing keeps reference <span className="font-mono">{issued.serial_number}</span> — this bumps the
              revision and regenerates the PDF.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Base tuition (₹)</label>
              <Input type="number" min="0" value={form.base_tuition} onChange={(e) => set("base_tuition")(e.target.value)}
                placeholder="e.g. 85000" className="mt-1 h-9 text-sm" data-testid="offer-base-tuition" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Scholarship / Concession (₹)</label>
              <Input type="number" min="0" value={form.scholarship_amount} onChange={(e) => set("scholarship_amount")(e.target.value)}
                placeholder="0" className="mt-1 h-9 text-sm" data-testid="offer-scholarship" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Seat Acceptance Deposit (₹)</label>
              <Input type="number" min="0" value={form.seat_deposit} onChange={(e) => set("seat_deposit")(e.target.value)}
                placeholder="e.g. 50000" className="mt-1 h-9 text-sm" data-testid="offer-seat-deposit" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Deposit due date</label>
              <Input type="text" value={form.due_date} onChange={(e) => set("due_date")(e.target.value)}
                placeholder="+14 days default" className="mt-1 h-9 text-sm" data-testid="offer-due-date" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-slate-600">Academic year</label>
              <Input type="text" value={form.academic_year} onChange={(e) => set("academic_year")(e.target.value)}
                placeholder="2026-2027 default" className="mt-1 h-9 text-sm" data-testid="offer-academic-year" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={form.apply_gst} onChange={(e) => set("apply_gst")(e.target.checked)} />
            Apply 18% GST on net tuition
          </label>
          <div className="flex gap-2 pt-1">
            <Button type="submit" className="bg-brand flex-1" disabled={busy || !eligible} data-testid="offer-submit">
              {busy ? "Generating…" : issued ? "Generate & Reissue Letter" : "Generate & Issue Letter"}
            </Button>
            {issued && <Button type="button" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>}
          </div>
        </form>
      )}
    </>
  );
}
